// Returns the value only when it is an http(s) URL, otherwise null. Use this to
// guard any sink that navigates to or renders a stored URL — `Linking.openURL`
// on native, an `<a href>` on web — so a `javascript:`, `data:`, `file:`, or
// custom-scheme value can't reach it. The recipe `sourceUrl` is a free-text
// field (and is also set by text/URL import and synced across devices), so the
// guard belongs at the sink, not just at input time.
export function safeHttpUrl(value: string | null | undefined): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  // Require an explicit http:// or https:// scheme followed by something. This
  // rejects other schemes, scheme-relative `//host`, and bare `javascript:...`.
  return /^https?:\/\/\S/i.test(trimmed) ? trimmed : null;
}

// Strip the PII-carrying parts of a URL so a failed-import URL can be logged to
// analytics (to prioritise/verify provider support) without leaking tracking or
// session data. Keeps scheme + host + path; drops the query string, the fragment,
// and any `user:pass@` userinfo. Returns null if the value isn't an http(s) URL.
export function sanitizeUrlForLog(value: string | null | undefined): string | null {
  const safe = safeHttpUrl(value);
  if (!safe) return null;
  const noHashOrQuery = safe.split("#")[0].split("?")[0];
  // Remove any userinfo (credentials) from the authority: scheme://user:pass@host → scheme://host
  return noHashOrQuery.replace(/^(https?:\/\/)[^@/]*@/i, "$1");
}
